import React, { useState, useEffect } from 'react';
import { Table, Button, Card, message, Space, Tag, Modal, Form, Input, InputNumber, Select, Popconfirm, Row, Col } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { dentistService } from '../../services';
import { useApp } from '../../context/AppContext';
import CurrencyDisplay from '../../components/CurrencyDisplay/CurrencyDisplay';
import CurrencySelector from '../../components/CurrencySelector/CurrencySelector';
import { getCurrencySymbol } from '../../utils/currency';

const { Option } = Select;

const Dentists = () => {
    const { t } = useTranslation('dentist');
    const { user, isAdmin } = useApp();
    const [dentists, setDentists] = useState([]);
    const [loading, setLoading] = useState(false);
    const [modalVisible, setModalVisible] = useState(false);
    const [editingDentist, setEditingDentist] = useState(null);
    const [form] = Form.useForm();

    useEffect(() => {
        fetchDentists();
    }, []);

    const fetchDentists = async () => {
        setLoading(true);
        try {
            const response = await dentistService.getAll({ limit: 1000 });
            if (response.success) {
                setDentists(response.data);
            }
        } catch (error) {
            message.error(t('fetchError'));
        } finally {
            setLoading(false);
        }
    };

    const handleAdd = () => {
        setEditingDentist(null);
        form.resetFields();
        setModalVisible(true);
    };

    const handleEdit = (record) => {
        setEditingDentist(record);
        form.setFieldsValue(record);
        setModalVisible(true);
    };

    const handleDelete = async (id) => {
        if (!isAdmin()) {
            message.error('Only administrators can delete dentists');
            return;
        }

        try {
            const response = await dentistService.delete(id);
            if (response.success) {
                message.success(t('deletedSuccess'));
                fetchDentists();
            } else {
                message.error(response.message || t('deleteError'));
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message;
            if (error.response?.status === 403) {
                message.error('You do not have permission to delete dentists');
            } else {
                message.error(errorMessage || t('deleteError'));
            }
            console.error('Delete error:', error);
        }
    };

    const handleSubmit = async (values) => {
        try {
            if (editingDentist) {
                // Remove password fields when editing
                const { password, confirmPassword, ...updateData } = values;
                await dentistService.update(editingDentist._id, updateData);
                message.success(t('updatedSuccess'));
            } else {
                // When creating, include createUserAccount flag and remove confirmPassword
                const { confirmPassword, ...createData } = values;
                createData.createUserAccount = true;
                await dentistService.create(createData);
                message.success(t('createdSuccess'));
            }
            setModalVisible(false);
            form.resetFields();
            fetchDentists();
        } catch (error) {
            message.error(editingDentist ? t('updateError') : t('createError'));
        }
    };

    const columns = [
        {
            title: t('name'),
            dataIndex: 'name',
            key: 'name'
        },
        {
            title: t('specialization'),
            dataIndex: 'specialization',
            key: 'specialization',
            render: (text) => <Tag color="blue">{text}</Tag>
        },
        {
            title: t('qualification'),
            dataIndex: 'qualification',
            key: 'qualification'
        },
        {
            title: t('consultationFee'),
            dataIndex: 'consultationFee',
            key: 'consultationFee',
            render: (fee, record) => (
                <CurrencyDisplay
                    amount={fee}
                    currency={record.currency || 'USD'}
                    showBoth={true}
                />
            )
        },
        {
            title: t('experience'),
            dataIndex: 'experience',
            key: 'experience',
            render: (exp) => exp ? `${exp} ${t('years')}` : 'N/A'
        },
        {
            title: t('availability'),
            dataIndex: 'availability',
            key: 'availability',
            render: (status) => (
                <Tag color={status === 'Available' ? 'success' : 'default'}>
                    {status === 'Available' ? t('available') : t('unavailable')}
                </Tag>
            )
        },
        {
            title: t('actions'),
            key: 'actions',
            render: (_, record) => (
                <Space>
                    {isAdmin() ? (
                        <>
                            <Button type="link" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
                                {t('edit')}
                            </Button>
                            <Popconfirm
                                title={t('deleteConfirm')}
                                onConfirm={() => handleDelete(record._id)}
                                okText={t('delete')}
                                cancelText={t('cancel')}
                            >
                                <Button type="link" danger icon={<DeleteOutlined />}>
                                    {t('delete')}
                                </Button>
                            </Popconfirm>
                        </>
                    ) : (
                        <span style={{ color: '#999' }}>-</span>
                    )}
                </Space>
            )
        }
    ];

    return (
        <Card>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
                <h1>{t('title')}</h1>
                {isAdmin() && (
                    <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
                        {t('addDentist')}
                    </Button>
                )}
            </div>
            <Table
                columns={columns}
                dataSource={dentists}
                rowKey="_id"
                loading={loading}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title={editingDentist ? t('editDentist') : t('addDentist')}
                open={modalVisible}
                onCancel={() => {
                    setModalVisible(false);
                    form.resetFields();
                }}
                onOk={() => form.submit()}
                okText={t('save')}
                cancelText={t('cancel')}
                width={600}
            >
                <Form
                    form={form}
                    layout="vertical"
                    onFinish={handleSubmit}
                >
                    <Form.Item
                        name="name"
                        label={t('name')}
                        rules={[{ required: true, message: 'Please enter name' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="specialization"
                        label={t('specialization')}
                        rules={[{ required: true, message: 'Please select specialization' }]}
                    >
                        <Select>
                            <Option value="General Dentistry">General Dentistry</Option>
                            <Option value="Orthodontics">Orthodontics</Option>
                            <Option value="Periodontics">Periodontics</Option>
                            <Option value="Endodontics">Endodontics</Option>
                            <Option value="Oral Surgery">Oral Surgery</Option>
                            <Option value="Pediatric Dentistry">Pediatric Dentistry</Option>
                            <Option value="Prosthodontics">Prosthodontics</Option>
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="qualification"
                        label={t('qualification')}
                        rules={[{ required: true, message: 'Please enter qualification' }]}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="experience"
                        label={t('experience')}
                    >
                        <InputNumber min={0} style={{ width: '100%' }} />
                    </Form.Item>

                    <Row gutter={16}>
                        <Col span={16}>
                            <Form.Item
                                name="consultationFee"
                                label={t('consultationFee')}
                                rules={[{ required: true, message: 'Please enter consultation fee' }]}
                            >
                                <InputNumber
                                    min={0}
                                    style={{ width: '100%' }}
                                    addonBefore={<Form.Item name="currency" noStyle>
                                        <CurrencySelector />
                                    </Form.Item>}
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item
                                name="currency"
                                label={t('currency.selectCurrency', { ns: 'common' })}
                                initialValue="USD"
                            >
                                <CurrencySelector />
                            </Form.Item>
                        </Col>
                    </Row>

                    <Form.Item
                        name="phone"
                        label={t('phone')}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="email"
                        label={t('email')}
                        rules={[
                            { required: !editingDentist, message: 'Please enter email' },
                            { type: 'email', message: 'Please enter a valid email' }
                        ]}
                    >
                        <Input type="email" />
                    </Form.Item>

                    {!editingDentist && (
                        <>
                            <Form.Item
                                name="password"
                                label={t('password')}
                                rules={[
                                    { required: true, message: 'Please enter password' },
                                    { min: 6, message: 'Password must be at least 6 characters' }
                                ]}
                            >
                                <Input.Password placeholder="Password for dentist to login" />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                label={t('confirmPassword')}
                                dependencies={['password']}
                                rules={[
                                    { required: true, message: 'Please confirm password' },
                                    ({ getFieldValue }) => ({
                                        validator(_, value) {
                                            if (!value || getFieldValue('password') === value) {
                                                return Promise.resolve();
                                            }
                                            return Promise.reject(new Error('Passwords do not match'));
                                        },
                                    }),
                                ]}
                            >
                                <Input.Password placeholder="Confirm password" />
                            </Form.Item>
                        </>
                    )}

                    <Form.Item
                        name="licenseNumber"
                        label={t('licenseNumber')}
                    >
                        <Input />
                    </Form.Item>

                    <Form.Item
                        name="availability"
                        label={t('availability')}
                        initialValue="Available"
                    >
                        <Select>
                            <Option value="Available">{t('available')}</Option>
                            <Option value="Unavailable">{t('unavailable')}</Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </Card>
    );
};

export default Dentists;
